(function bceLikedProductsApp() {
  'use strict';

  const LEGACY_KEY = 'bce-wishlist-v1';
  const STORAGE_PREFIX = 'bce-liked-products-v2';
  const MAX_ITEMS = 80;
  const customerId = String(document.documentElement.dataset.bceCustomerId || '').replace(/\D/g, '').slice(0, 32);
  const guestKey = `${STORAGE_PREFIX}:guest`;
  const storageKey = customerId ? `${STORAGE_PREFIX}:customer:${customerId}` : guestKey;
  const hydrationInFlight = new Map();
  const productSyncTimes = new Map();
  const PRODUCT_SYNC_TTL = 15000;
  let refreshQueued = false;
  let drawerRefreshQueued = false;
  let drawerRenderInProgress = false;
  let likedDrawerOpen = false;
  let lastDrawerTrigger = null;

  function safeProductPath(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return '';
      return url.pathname + url.search;
    } catch {
      return '';
    }
  }

  function safeImageUrl(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      const host = url.hostname.toLowerCase();
      const allowed = url.protocol === 'https:' && (
        url.origin === window.location.origin ||
        host === 'cdn.shopify.com' ||
        host.endsWith('.shopifycdn.net') ||
        host.endsWith('.myshopify.com')
      );
      return allowed ? url.href : '';
    } catch {
      return '';
    }
  }

  function itemKey(item) {
    return String(item?.handle || item?.url || '').trim().toLowerCase();
  }

  function normaliseItem(item) {
    if (!item || typeof item !== 'object') return null;
    const suppliedHandle = String(item.handle || '').trim().toLowerCase();
    const url = safeProductPath(item.url || (suppliedHandle ? `/products/${suppliedHandle}` : ''));
    const handleFromUrl = url.match(/\/products\/([^/?#]+)/i)?.[1] || '';
    const handle = suppliedHandle || handleFromUrl.trim().toLowerCase();
    if (!handle || !url) return null;
    return {
      handle,
      title: String(item.title || 'Product').trim().slice(0, 240),
      url,
      image: safeImageUrl(item.image),
      price: String(item.price ?? '').trim().slice(0, 80),
      variantId: String(item.variantId || '').replace(/\D/g, '').slice(0, 32),
      likedAt: String(item.likedAt || item.addedAt || new Date().toISOString()),
    };
  }

  function readKey(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.map(normaliseItem).filter(Boolean).slice(0, MAX_ITEMS) : [];
    } catch {
      return [];
    }
  }

  function readItems() {
    return readKey(storageKey);
  }

  function uniqueItems(groups) {
    const found = new Set();
    const items = [];
    groups.flat().forEach((candidate) => {
      const item = normaliseItem(candidate);
      const key = itemKey(item);
      if (!item || !key || found.has(key)) return;
      found.add(key);
      items.push(item);
    });
    return items.slice(0, MAX_ITEMS);
  }

  function writeItems(items, announce = true) {
    const safeItems = uniqueItems([items]);
    try {
      localStorage.setItem(storageKey, JSON.stringify(safeItems));
    } catch {
      return false;
    }
    if (announce) {
      window.dispatchEvent(new CustomEvent('bce:liked-products-changed', {
        detail: { items: safeItems, storageKey },
      }));
    }
    return true;
  }

  function migrateStoredItems() {
    const current = readItems();
    const imported = [];

    if (customerId) imported.push(...readKey(guestKey));
    imported.push(...readKey(LEGACY_KEY));

    const merged = uniqueItems([current, imported]);
    if (merged.length || current.length || imported.length) writeItems(merged, false);

    try {
      if (customerId) localStorage.removeItem(guestKey);
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // Storage cleanup must not prevent the Like feature from loading.
    }
  }

  function shopRoot() {
    return String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  }

  function fetchProductDetails(item) {
    const key = itemKey(item);
    if (!key) return Promise.resolve(item);
    if (hydrationInFlight.has(key)) return hydrationInFlight.get(key);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 6500);
    const request = fetch(`${shopRoot()}products/${encodeURIComponent(item.handle)}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load product details');
        return response.json();
      })
      .then((product) => {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const savedVariantId = String(item.variantId || '').replace(/\D/g, '');
        const variant = variants.find((candidate) => String(candidate?.id || '') === savedVariantId)
          || variants.find((candidate) => candidate?.available)
          || variants[0];
        const variantId = String(variant?.id || '').replace(/\D/g, '');
        if (!variantId) return item;

        const cents = Number(variant?.price ?? product?.price);
        const price = Number.isFinite(cents) ? String(cents / 100) : item.price;
        const variantImage = variant?.featured_image?.src || variant?.featured_image;
        const image = variantImage || product?.featured_image || product?.images?.[0] || item.image || '';
        return normaliseItem({
          ...item,
          title: product?.title || item.title,
          url: product?.url || item.url,
          image,
          price,
          variantId,
        }) || item;
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        hydrationInFlight.delete(key);
      });

    hydrationInFlight.set(key, request);
    return request;
  }

  function productDataChanged(current, fresh) {
    return ['title', 'url', 'image', 'price', 'variantId'].some((field) => current?.[field] !== fresh?.[field]);
  }

  function syncCurrentProductData(items, force = false) {
    const now = Date.now();
    const due = items.filter((item) => {
      const key = itemKey(item);
      if (!key) return false;
      return force || now - (productSyncTimes.get(key) || 0) >= PRODUCT_SYNC_TTL;
    });
    if (!due.length) return;

    due.forEach((item) => productSyncTimes.set(itemKey(item), now));

    Promise.allSettled(due.map((item) => fetchProductDetails(item))).then((results) => {
      const current = readItems();
      let changed = false;

      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value?.variantId) return;
        const key = itemKey(result.value);
        const index = current.findIndex((item) => itemKey(item) === key);
        if (index < 0 || !productDataChanged(current[index], result.value)) return;
        current[index] = result.value;
        changed = true;
      });

      if (changed) writeItems(current);
    });
  }

  function itemFromButton(button) {
    const variantInput = button.closest('.buy-buttons-block')?.querySelector('input[name="id"]');
    return normaliseItem({
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      variantId: variantInput?.value || button.dataset.productVariantId,
      likedAt: new Date().toISOString(),
    });
  }

  function updateCount(items) {
    document.querySelectorAll('[data-bce-liked-count]').forEach((badge) => {
      badge.textContent = String(items.length);
      badge.hidden = items.length === 0;
    });
  }

  function refreshButtons(items = readItems()) {
    const keys = new Set(items.map(itemKey));
    document.querySelectorAll('[data-bce-like-toggle]').forEach((button) => {
      const key = String(button.dataset.productHandle || '').trim().toLowerCase();
      const active = Boolean(key && keys.has(key));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const title = String(button.dataset.productTitle || 'product');
      button.setAttribute('aria-label', active ? `Unlike ${title}` : `Like ${title}`);
      button.title = active ? 'Unlike product' : 'Like product';
      const label = button.querySelector('[data-bce-like-label]');
      if (label) label.textContent = active ? 'Liked' : 'Like';
    });
    updateCount(items);
  }

  function showFeedback(source, message, kind = 'like') {
    if (window.BCEProductFeedback?.show) {
      window.BCEProductFeedback.show(source, message, kind, 1350);
      return;
    }
    const scope = source.closest('product-card, .resource-card, .buy-buttons-block, .bce-liked-card') || source.parentElement;
    if (!scope) return;
    scope.querySelector('[data-bce-action-feedback]')?.remove();
    const feedback = document.createElement('div');
    feedback.className = 'bce-action-feedback';
    feedback.dataset.bceActionFeedback = '';
    feedback.dataset.kind = kind;
    feedback.setAttribute('role', 'status');
    feedback.textContent = message;
    scope.appendChild(feedback);
    window.setTimeout(() => feedback.remove(), 1350);
  }

  function formatPrice(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value || '');
    return `Rs. ${number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function makeLikeButton(item) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bce-card-actions__like is-active';
    button.dataset.bceLikeToggle = '';
    button.dataset.productHandle = item.handle;
    button.dataset.productTitle = item.title;
    button.dataset.productUrl = item.url;
    button.dataset.productImage = item.image;
    button.dataset.productPrice = item.price;
    button.dataset.productVariantId = item.variantId;
    button.setAttribute('aria-pressed', 'true');
    button.setAttribute('aria-label', `Unlike ${item.title}`);
    button.title = 'Unlike product';
    button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path class="bce-like-fill" d="M8 10.3 11.5 4c.55-1 2.05-.62 2.08.52l.05 2.12c.02.92-.2 1.83-.65 2.63h4.65c1.42 0 2.48 1.31 2.18 2.7l-1.2 5.65a2.25 2.25 0 0 1-2.2 1.78H8v-9.1Z"></path><path d="M3.5 10.3H8v9.1H3.5a1 1 0 0 1-1-1v-7.1a1 1 0 0 1 1-1Z"></path></svg>';
    return button;
  }

  function cartIconMarkup() {
    return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 4.5h2l1.7 9.1a2 2 0 0 0 2 1.65h7.9a2 2 0 0 0 1.94-1.52L20.5 8H6.2"></path><circle cx="9.5" cy="19" r="1.25" fill="currentColor" stroke="none"></circle><circle cx="16.5" cy="19" r="1.25" fill="currentColor" stroke="none"></circle></svg>';
  }

  function makeLikedCard(item) {
    const card = document.createElement('article');
    card.className = 'bce-liked-card';

    const media = document.createElement('a');
    media.className = 'bce-liked-card__media';
    media.href = item.url;
    media.setAttribute('aria-label', `View ${item.title}`);
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 320;
      image.height = 320;
      media.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.textContent = 'BCE World';
      media.appendChild(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'bce-liked-card__body';
    const title = document.createElement('a');
    title.className = 'bce-liked-card__title';
    title.href = item.url;
    title.textContent = item.title;
    body.appendChild(title);

    if (item.price) {
      const price = document.createElement('p');
      price.className = 'bce-liked-card__price';
      price.textContent = formatPrice(item.price);
      body.appendChild(price);
    }

    const actions = document.createElement('div');
    actions.className = 'bce-card-actions bce-liked-card__actions';
    actions.dataset.bceCardActions = '';
    actions.appendChild(makeLikeButton(item));

    const variantId = String(item.variantId || '').replace(/\D/g, '');
    if (variantId) {
      const buy = document.createElement('a');
      buy.className = 'bce-card-actions__buy';
      buy.href = `/cart/${variantId}:1?checkout`;
      buy.dataset.bceBuyNow = '';
      buy.dataset.variantId = variantId;
      buy.setAttribute('aria-label', `Buy ${item.title} now`);
      buy.textContent = 'Buy now';
      actions.appendChild(buy);

      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.className = 'bce-card-actions__cart';
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.value = variantId;
      const quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.value = '1';
      const add = document.createElement('button');
      add.type = 'submit';
      add.name = 'add';
      add.className = 'bce-card-actions__cart-button';
      add.dataset.bceCardAdd = '';
      add.dataset.variantId = variantId;
      add.setAttribute('aria-label', `Add ${item.title} to cart`);
      add.title = 'Add to cart';
      add.innerHTML = cartIconMarkup();
      form.append(idInput, quantityInput, add);
      actions.appendChild(form);
    } else {
      const view = document.createElement('a');
      view.className = 'bce-card-actions__buy';
      view.href = item.url;
      view.setAttribute('aria-label', `View ${item.title}`);
      view.textContent = 'View';
      actions.appendChild(view);

      const cartShell = document.createElement('span');
      cartShell.className = 'bce-card-actions__cart';
      const pendingCart = document.createElement('button');
      pendingCart.type = 'button';
      pendingCart.disabled = true;
      pendingCart.setAttribute('aria-label', `Loading cart option for ${item.title}`);
      pendingCart.title = 'Loading cart option';
      pendingCart.innerHTML = cartIconMarkup();
      cartShell.appendChild(pendingCart);
      actions.appendChild(cartShell);
    }

    body.appendChild(actions);
    card.append(media, body);
    return card;
  }

  function renderLikedProductsDrawer(items = readItems()) {
    const root = document.querySelector('[data-bce-liked-drawer]');
    if (!(root instanceof HTMLDialogElement)) return;
    const grid = root.querySelector('[data-bce-liked-drawer-items]');
    const empty = root.querySelector('[data-bce-liked-drawer-empty]');
    const status = root.querySelector('[data-bce-liked-drawer-status]');
    const clear = root.querySelector('[data-bce-clear-liked]');
    if (!grid || !empty) return;

    drawerRenderInProgress = true;
    try {
      grid.replaceChildren();
      empty.hidden = items.length > 0;
      grid.hidden = items.length === 0;
      if (clear instanceof HTMLButtonElement) clear.hidden = items.length === 0;
      if (status) {
        status.textContent = items.length
          ? `${items.length} liked ${items.length === 1 ? 'product' : 'products'}`
          : 'No liked products';
      }

      items.forEach((item) => grid.appendChild(makeLikedCard(item)));
    } finally {
      queueMicrotask(() => {
        drawerRenderInProgress = false;
      });
    }
    if (likedDrawerOpen) syncCurrentProductData(items);
  }

  function bindDrawer(drawer) {
    if (!(drawer instanceof HTMLDialogElement) || drawer.dataset.bceLikedDrawerBound === 'true') return;
    drawer.dataset.bceLikedDrawerBound = 'true';

    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) closeDrawer();
    });
    drawer.addEventListener('close', () => {
      likedDrawerOpen = false;
      lastDrawerTrigger?.focus();
      lastDrawerTrigger = null;
    });
  }

  function closeDrawer() {
    const drawer = document.querySelector('[data-bce-liked-drawer]');
    likedDrawerOpen = false;
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
    drawer.close();
  }

  function openDrawer(trigger) {
    document.dispatchEvent(new CustomEvent('bce:header-panel-opening', { detail: { source: 'liked' } }));
    const drawer = document.querySelector('[data-bce-liked-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;
    lastDrawerTrigger = trigger instanceof HTMLElement ? trigger : null;
    likedDrawerOpen = true;
    productSyncTimes.clear();
    renderLikedProductsDrawer();
    document.querySelector('[data-bce-header-more-menu]')?.classList.remove('is-open');
    document.querySelector('[data-bce-header-more-toggle]')?.setAttribute('aria-expanded', 'false');
    bindDrawer(drawer);
    if (!drawer.open) {
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
    }
    window.requestAnimationFrame(() => drawer.querySelector('[data-bce-liked-drawer-close]')?.focus());
  }

  function refreshAll(items = readItems()) {
    refreshButtons(items);
    renderLikedProductsDrawer(items);
  }

  function guardLikePointer(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-bce-like-toggle]') instanceof HTMLButtonElement) {
      event.stopImmediatePropagation();
    }
  }

  document.addEventListener('pointerdown', guardLikePointer, true);
  document.addEventListener('pointerup', guardLikePointer, true);
  document.addEventListener('touchstart', guardLikePointer, { capture: true, passive: true });
  document.addEventListener('touchend', guardLikePointer, { capture: true, passive: true });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    const drawerTrigger = target?.closest('[data-bce-liked-drawer-trigger]');
    if (drawerTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer(drawerTrigger);
      return;
    }

    const drawerClose = target?.closest('[data-bce-liked-drawer-close]');
    if (drawerClose instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDrawer();
      return;
    }

    const clear = target?.closest('[data-bce-clear-liked]');
    if (clear instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (writeItems([])) refreshAll([]);
      return;
    }

    const toggle = target?.closest('[data-bce-like-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const item = itemFromButton(toggle);
    const key = itemKey(item);
    if (!item || !key || !item.url) return;

    const items = readItems();
    const existingIndex = items.findIndex((saved) => itemKey(saved) === key);
    const nowLiked = existingIndex < 0;
    if (nowLiked) items.unshift(item);
    else items.splice(existingIndex, 1);

    showFeedback(toggle, nowLiked ? 'Liked' : 'Like removed', 'like');
    if (writeItems(items)) refreshAll(items);
  }, true);

  window.addEventListener('bce:liked-products-changed', (event) => {
    if (event.detail?.storageKey === storageKey) refreshAll(event.detail.items || readItems());
  });
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === LEGACY_KEY || (customerId && event.key === guestKey)) refreshAll();
  });
  window.addEventListener('pageshow', () => {
    productSyncTimes.clear();
    refreshAll();
  });

  function containsFreshLikedDrawerMarkup(node) {
    if (!(node instanceof Element)) return false;
    return (
      node.matches('[data-bce-liked-drawer], [data-bce-liked-drawer-items]') ||
      Boolean(node.querySelector('[data-bce-liked-drawer], [data-bce-liked-drawer-items]'))
    );
  }

  const observer = new MutationObserver((mutations) => {
    if (
      !drawerRenderInProgress &&
      mutations.some(
        (mutation) =>
          (mutation.target instanceof Element && mutation.target.matches('[data-bce-liked-drawer-items]')) ||
          Array.from(mutation.addedNodes).some((node) => containsFreshLikedDrawerMarkup(node))
      )
    ) {
      drawerRefreshQueued = true;
    }

    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(() => {
      refreshQueued = false;
      refreshButtons();

      if (!drawerRefreshQueued) return;
      drawerRefreshQueued = false;

      const drawer = document.querySelector('[data-bce-liked-drawer]');
      bindDrawer(drawer);
      renderLikedProductsDrawer();

      if (likedDrawerOpen && drawer instanceof HTMLDialogElement && !drawer.open) {
        if (typeof drawer.showModal === 'function') drawer.showModal();
        else drawer.setAttribute('open', '');
      }
    });
  });

  function init() {
    migrateStoredItems();
    refreshAll();
    bindDrawer(document.querySelector('[data-bce-liked-drawer]'));
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }

  window.BCELikedProducts = Object.freeze({
    items: () => readItems().map((item) => ({ ...item })),
    refresh: () => refreshAll(),
    open: () => openDrawer(),
    close: () => closeDrawer(),
    clear: () => {
      if (writeItems([])) refreshAll([]);
    },
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
